import { useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import type { RelayChatExtension, RunStore } from '@inference.engine/agentic-react';
import { useUIStore } from '@/app/store/ui';
import { useUIContextStore } from '@/app/store/ui-context';
import { useWidgetStore } from '@/app/store/widget';
import {
  ApprovalPrompt,
  QuestionAnnouncer,
  QuestionPrompt,
  usePauseDecisions,
} from '@/entities/tool-approval';
import { ProjectContextPicker, useProjectContextRows } from '@/features/add-project-context';
import { ThreadWithCompactions } from './ThreadWithCompactions';
import { useAiChat, type UseAiChatDeps } from '@/features/ai';
import {
  ChatSessionLayout,
  ChatThread,
  dropMaskFromOptions,
  generateChatTitle,
  persistTemplatesSettingsFromStore,
  processInputsWithMask,
  useChatHistory,
  useChatStore,
  useChatStoreApi,
  type Message,
  type SendMessageInput,
} from '@/features/chats';
import { track } from '@/shared/analytics';

// Dependency injection: `features/chats` (ChatInput) is a canonical-leaf slice
// that MUST NOT import the sibling `add-project-context` feature. The page is
// the layer allowed to bind features, so it threads the picker + rows hook
// down through ChatSessionLayout. Module-level stable reference so the deps
// bag identity never changes per render (mirrors INTRO_TOOL_CATEGORIES_DEPS).
export const PROJECT_CONTEXT_DEPS = {
  ProjectContextPicker,
  useProjectContextRows,
};

export interface ChatSessionProps {
  chatId: string;
  workspaceId: string;
  /** The run store the stream reduces into, so a debug panel beside the chat can read it. */
  runStore: RunStore;
  extensions?: readonly RelayChatExtension[];
  /** Fields every send of this conversation carries, read at send time. */
  runOptions?: (contextId: string) => Record<string, unknown> | undefined;
  /** The message that opened the conversation, sent once its empty history has loaded. */
  initialMessage?: SendMessageInput;
  /** Runs before every send, such as saving what the message should run against. */
  beforeSend?: () => Promise<void>;
  /** Shown in the empty thread before the first message. */
  empty?: ReactNode;
  /**
   * Drawn at the end of the row under the input, such as how full the agent's context is. A
   * function is told the conversation and whether a turn is under way.
   */
  footerEnd?: ReactNode | ((chat?: { chatId: string; busy: boolean }) => ReactNode);
  /** Marks where the conversation was summarized, as the builder's Test screen does. */
  showCompactions?: boolean;
  /** The builder's debug chat settings beside the model picker; see the input's own prop. */
  debugControls?: { models: readonly string[]; effort?: string };
  /** Whether files dropped anywhere on the page attach to this conversation. Defaults to true. */
  dropzone?: boolean;
  /**
   * Whether the first message names the conversation. Defaults to true; off for one opened with a
   * title of its own, which the generated one would replace.
   */
  titleFromFirstMessage?: boolean;
  /** What each send tells the agent about the screen. Defaults to the app's UI context. */
  uiContext?: () => unknown;
  /**
   * Whether widgets the agent opens go to the app's widget panel. Off for a conversation that runs
   * beside another one on the page and opens none, so it never clears or closes that one's widgets.
   */
  widgets?: boolean;
  /** Called each time a turn stops streaming: finished, failed or stopped. */
  onTurnEnd?: () => void;
  /** Whether the conversation counts in product analytics. Defaults to true. */
  analytics?: boolean;
}

const KEEP_TITLE = () => Promise.resolve();
const noop = () => undefined;
/** The widget store of a conversation whose widgets go nowhere. */
const NO_WIDGETS = {
  clearActiveVersion: noop,
  clearWidgetPanel: noop,
  processWidget: noop,
  reset: noop,
  storeWidgetVersion: noop,
};
const useNoWidgets = () => NO_WIDGETS;
const noWidgetState = () => ({ widget: null });
const noTrack: typeof track = () => undefined;

/**
 * One conversation as the chat page draws it: the thread, its widgets and approvals, and the
 * composer. Every surface that talks to an agent renders this, so a change to the chat reaches all
 * of them. It reads the chat store of its `ChatStoreScope`, else the app's; a second one on a page
 * needs a scope of its own, and `widgets` off unless it is the one that opens widgets.
 */
export function ChatSession({
  chatId,
  workspaceId,
  runStore,
  extensions,
  runOptions,
  footerEnd,
  showCompactions = false,
  debugControls,
  initialMessage,
  beforeSend,
  empty,
  dropzone = true,
  titleFromFirstMessage = true,
  uiContext,
  widgets = true,
  analytics = true,
  onTurnEnd,
}: ChatSessionProps) {
  const chatStore = useChatStoreApi();
  const trackEvent = analytics ? track : noTrack;
  const runOptionsRef = useRef(runOptions);
  runOptionsRef.current = runOptions;
  const uiContextRef = useRef(uiContext);
  uiContextRef.current = uiContext;
  // === Dependency injection ===
  // `useAiChat` is the page-orchestration hook for the AI chat session. It
  // depends on chats (`useChatStore`, `useChatHistory`, helpers) and on
  // app-level stores (`useWidgetStore`, `useUIStore`, `useUIContextStore`).
  // To keep `features/ai` cross-feature-OUT and cross-app-OUT clean, the
  // page wires the deps and threads them down via the typed `deps` bag.
  const aiChatDeps = useMemo<UseAiChatDeps>(
    () => ({
      chats: {
        useChatStoreHook: useChatStore,
        getChatStoreState: chatStore.getState,
        setChatStoreState: chatStore.setState as (partial: Record<string, unknown>) => void,
        useChatHistoryHook: useChatHistory,
        generateChatTitle: titleFromFirstMessage ? generateChatTitle : KEEP_TITLE,
        dropMaskFromOptions,
        processInputsWithMask,
      },
      app: {
        useWidgetStoreHook: widgets ? useWidgetStore : useNoWidgets,
        getWidgetStoreState: widgets ? useWidgetStore.getState : noWidgetState,
        getUIContextSnapshot: () =>
          uiContextRef.current
            ? uiContextRef.current()
            : useUIContextStore.getState().getContextSnapshot(),
      },
      abortHandler: {
        setInput: chatStore.getState().setInput,
        setWidgetMessages: widgets ? useWidgetStore.getState().setWidgetMessages : noop,
        clearWidget: widgets ? useWidgetStore.getState().clearWidget : noop,
        closeSidePanel: widgets ? useUIStore.getState().closeSidePanel : noop,
      },
      ...(extensions ? { extensions } : {}),
      runStore,
      runOptions: (contextId) => runOptionsRef.current?.(contextId),
      track: trackEvent,
    }),
    [chatStore, extensions, runStore, titleFromFirstMessage, trackEvent, widgets],
  );

  const {
    messages,
    isNewChat,
    isLoading,
    sendMessage: send,
    status,
    answerApprovals,
    stop,
  } = useAiChat(chatId, workspaceId, aiChatDeps);
  const beforeSendRef = useRef(beforeSend);
  beforeSendRef.current = beforeSend;
  const sendMessage = useMemo(
    () => async (input: SendMessageInput) => {
      await beforeSendRef.current?.();
      await send(input);
    },
    [send],
  );
  const { t } = useTranslation('chats');
  const compactionNotice = useBooleanFlagValue('chat_compaction_notice', false);
  const compacting = useSyncExternalStore(runStore.subscribe, () => runStore.getState().compacting);
  const isStreaming = status === 'submitted' || status === 'streaming';
  const onTurnEndRef = useRef(onTurnEnd);
  onTurnEndRef.current = onTurnEnd;
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (wasStreaming.current && !isStreaming) onTurnEndRef.current?.();
    wasStreaming.current = isStreaming;
  }, [isStreaming]);
  const pause = usePauseDecisions({
    contextId: chatId,
    busy: isStreaming,
    onAnswer: (decisions) => answerApprovals(decisions),
  });
  // The server refuses a new message while a turn waits on a tool approval, so the composer says
  // so up front and keeps what was typed. Questions do not hold it on their own, but once the
  // approval is decided the pause resumes only with their answers, so those are what it waits on.
  const sendBlockedReason = !pause.blocksMessages
    ? undefined
    : t(
        pause.approvalOpen
          ? 'toolApprovals.chat.answerFirst'
          : 'toolApprovals.chat.answerQuestionsFirst',
      );
  const busy = isStreaming || pause.blocksMessages || pause.questions.length > 0;
  const footer = typeof footerEnd === 'function' ? footerEnd({ chatId, busy }) : footerEnd;
  const setAgentById = useChatStore((s) => s.setAgentById);

  const setSender = useChatStore((s) => s.setSender);
  const setInputOptions = useChatStore((s) => s.setInputOptions);
  const currentAgentId = useChatStore((s) => s.currentAgentId);

  // Guards the one-time initial message + agent dispatch below.
  const isInitialMessageSent = useRef(false);

  // — Analytics: started vs resumed detection (mutually exclusive via isNewChat) —
  const didTrackStart = useRef(false);
  const didTrackResume = useRef(false);
  useEffect(() => {
    if (!chatId || !currentAgentId) return;
    if (isNewChat) {
      if (didTrackStart.current) return;
      trackEvent('agent_session_started', { agent_id: currentAgentId });
      didTrackStart.current = true;
    } else {
      if (didTrackResume.current) return;
      trackEvent('agent_session_resumed', { session_id: chatId, agent_id: currentAgentId });
      didTrackResume.current = true;
    }
  }, [chatId, currentAgentId, isNewChat, trackEvent]);

  // — Analytics: session ended (navigation / close) —
  const messageCountRef = useRef(0);
  useEffect(() => {
    messageCountRef.current = messages.length;
  }); // intentionally no deps — runs every render to track latest

  // Capture the agent_id only on the FIRST availability; do not update on handoffs.
  // agent_session_ended is attributed to the session's starting agent — handoff events
  // already document the journey.
  const endAgentIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!endAgentIdRef.current && currentAgentId) {
      endAgentIdRef.current = currentAgentId;
    }
  });

  // Dep array intentionally excludes currentAgentId: including it would cause the cleanup
  // to fire agent_session_ended on every handoff (currentAgentId changes → cleanup runs →
  // spurious ended event). We capture the starting agent_id via endAgentIdRef above.
  // hasEndedRef prevents double-fire across StrictMode (dev) and the close+navigation race
  // when a real tab close triggers both beforeunload AND unmount cleanup.
  const hasEndedRef = useRef(false);
  useEffect(() => {
    if (!chatId) return;
    const sessionId = chatId;
    const fireEnded = (endedVia: 'navigation' | 'close') => {
      if (hasEndedRef.current) return;
      const aid = endAgentIdRef.current;
      if (!aid) return;
      hasEndedRef.current = true;
      trackEvent('agent_session_ended', {
        session_id: sessionId,
        agent_id: aid,
        message_count: messageCountRef.current,
        ended_via: endedVia,
      });
    };
    const onBeforeUnload = () => fireEnded('close');
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      fireEnded('navigation');
    };
  }, [chatId, trackEvent]);

  // Set the override when component mounts, clean up on unmount
  // This is a bit tricky because of how heavily the FE states are tied across non-chat pages.
  useEffect(() => {
    setSender(sendMessage, { analytics });

    return () => {
      setSender(null);
    };
  }, [analytics, sendMessage, setSender]);

  // chatId is the signal `sendOrCreateChat` reads to decide between sending through the
  // mounted chat page and creating a new chat. The sender it implies is cleared on unmount
  // but chatId was not, so a return visit to a non-chat page sent through a dead sender and
  // silently dropped. `chatStore` from context is stable, so this cleanup fires only on
  // real unmount; A→B navigation between chats stays mounted and prepareChatHistory
  // re-sets chatId for the new conversation.
  useEffect(() => {
    return () => {
      chatStore.setState({ chatId: null });
    };
  }, [chatStore]);

  // Handle sending initial message after connection is established
  // This is ugly, we need to clean this logic up in a central provider that manages the chat stream related client logic including loading the chat, settings the agents, and managing the send logic with the stream hooks provided by the vercel AI SDK.
  useEffect(() => {
    if (isLoading) return;

    if (isNewChat && initialMessage && !isInitialMessageSent.current) {
      isInitialMessageSent.current = true;
      void setAgentById(initialMessage.agentId).then(() => {
        const existingInputs = initialMessage.inputs;
        const knowledgeBaseIds = initialMessage.knowledgeBaseIds;

        setInputOptions({
          ...(existingInputs || {}),
          ...(knowledgeBaseIds?.length && {
            knowledgeBaseIds: knowledgeBaseIds,
          }),
        });

        // New-chat first dispatch: `chatId` now exists and options are fully populated (incl. campaign),
        // so snapshot them into `session:<chatId>` — otherwise resuming this session would have no entry
        // to restore. No-op for non-templates.
        persistTemplatesSettingsFromStore();

        // Switch from fullscreen to split when sending a message
        const uiState = useUIStore.getState();
        if (uiState.sidePanelType === 'widget' && uiState.sidePanelLayoutMode === 'fullscreen') {
          uiState.setSidePanelLayoutMode('split');
        }

        // The store's sendMessage action — where agent_message_sent is tracked —
        // is bypassed here because the AI SDK's sendMessage is called directly for
        // the initial dispatch. Track explicitly so the opener (often the most
        // important message of the session) isn't dropped from the funnel.
        const projectCtx = initialMessage.projectContext;
        trackEvent('agent_message_sent', {
          agent_id: initialMessage.agentId,
          session_id: chatId,
          message_length: initialMessage.prompt.length,
          has_attachment:
            (initialMessage.files?.length ?? 0) > 0 ||
            (projectCtx?.briefs.length ?? 0) > 0 ||
            (projectCtx?.artifactIds.length ?? 0) > 0,
        });

        // Ensure knowledgeBaseIds are included in the sendMessage call
        void send({
          ...initialMessage,
          knowledgeBaseIds: knowledgeBaseIds,
        });
      });
    }
  }, [
    isLoading,
    initialMessage,
    send,
    setAgentById,
    isNewChat,
    setInputOptions,
    chatId,
    trackEvent,
  ]);

  const thread = {
    messages: messages as unknown as Message[],
    footer: (
      <div className="mt-sm">
        <ApprovalPrompt pause={pause} />
      </div>
    ),
  };

  return (
    <ChatSessionLayout
      onSend={sendMessage}
      {...(footer ? { footerEnd: footer } : {})}
      {...(debugControls ? { debugControls } : {})}
      {...(pause.questions.length > 0 ? { inputTop: <QuestionPrompt pause={pause} /> } : {})}
      {...(sendBlockedReason ? { sendBlockedReason } : {})}
      {...(compactionNotice && compacting && isStreaming
        ? { busyLabel: t('chats.chatInputV2.summarizingEarlierMessages') }
        : {})}
      onStop={() => void stop()}
      isNewChat={false}
      projectContextDeps={PROJECT_CONTEXT_DEPS}
      dropzone={dropzone}
    >
      {/*
       * `useAiChat` returns the loose `ChatMessageLike[]` mirror to keep
       * `features/ai` cross-feature-OUT clean . The shape is
       * structurally compatible with chats's strict `Message`, so we
       * narrow at the page boundary where both contracts are visible.
       */}
      <QuestionAnnouncer pause={pause} />
      {messages.length === 0 && empty ? (
        empty
      ) : showCompactions || compactionNotice ? (
        // Mounted from the start rather than once a turn is stored: swapping thread components
        // remounts every message, which fades the whole thread in again.
        <ThreadWithCompactions {...thread} chatId={chatId} runStore={runStore} />
      ) : (
        <ChatThread {...thread} />
      )}
    </ChatSessionLayout>
  );
}
